import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { IMCMapView } from '../../../classes/mcMapView';
import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, NumberConverter } from '../../../inputConverter';
import { GisToolsService } from '../../../services/gis-tools.service';
import { IsleService } from '../../../services/isle.service';
import { BaseComponent } from '../../base.component';

import { ISegmentData, Segment, SegmentData } from './isle.model';

let Graphic: __esri.GraphicConstructor;
let Point: __esri.PointConstructor;
let SpatialReference: __esri.SpatialReferenceConstructor;

/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-isle-ng',
    styleUrls: ['../../sharedStyles/base.scss', './isle.component.scss'],
    templateUrl: './isle.component.html'
})
export class IsleComponent extends BaseComponent implements OnInit {
    private _active: boolean = false;
    @Input()
    set active(value: boolean) {
        this._active = BooleanConverter(value);
        this._active ? this.activate() : this.deactivate();
        this.activeChange.emit(this._active);
    }

    _centerMapZoomLevel = 10;
    @Input()
    set centerMapZoomLevel(value: number) {
        this._centerMapZoomLevel = NumberConverter(value);
    }
    get centerMapZoomLevel() {
        return this._centerMapZoomLevel;
    }

    _enableAutoZoomLevel = true;
    @Input()
    set enableAutoZoomLevel(value: boolean) {
        this._enableAutoZoomLevel = BooleanConverter(value);
    }
    get enableAutoZoomLevel() {
        return this._enableAutoZoomLevel;
    }

    _pointSymbol: __esri.symbols.SimpleMarkerSymbol;
    @Input()
    set pointSymbol(symbol) {
        this._pointSymbol = symbol;
    }

    _lineSymbol: __esri.symbols.SimpleLineSymbol;
    @Input()
    set lineSymbol(symbol) {
        this._lineSymbol = symbol;
    }

    _moveMapOnClick = true;
    @Input()
    set moveMapOnClick(value: boolean) {
        this._moveMapOnClick = BooleanConverter(value);
    }

    _includeToolbarButtons = true;
    @Input()
    set includeToolbarButtons(value: boolean) {
        this._includeToolbarButtons = BooleanConverter(value);
    }

    _includeSegmentSection = true;
    @Input()
    set includeSegmentSection(value: boolean) {
        this._includeSegmentSection = BooleanConverter(value);
    }
    get includeSegmentSection() {
        return this._includeSegmentSection;
    }

    _includeScaleSection = false;
    @Input()
    set includeScaleSection(value: boolean) {
        this._includeScaleSection = BooleanConverter(value);
    }
    get includeScaleSection() {
        return this._includeScaleSection;
    }

    _mileMarkerDescription = '';
    @Input()
    set mileMarkerDescription(value: string) {
        if (value) {
            this._mileMarkerDescription = ` {    MM -   ${value}   }`;
        } else {
            this._mileMarkerDescription = '';
        }
    }
    get mileMarkerDescription() {
        return this._mileMarkerDescription;
    }

    _showStreetNames: boolean = false;
    @Input()
    set showStreetNames(value: boolean) {
        this._showStreetNames = BooleanConverter(value);
    }
    get showStreetNames() {
        return this._showStreetNames;
    }

    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();

    mapRef: IMCMapView;
    selectedPoint: __esri.geometry.Point;
    mapClickListener: IHandle;
    showSelect: boolean = false;
    templateOptions = [];
    selectedTemplate: string = 'PAVEMENT HISTORY';

    selectedAttributeItem;
    itemDetailsOpen;

    // Isle Properties
    segmentData: SegmentData = null;
    selectedSegment: Segment;
    public options: any = {
        crsTolerance: 24
    };
    centerPoint: __esri.geometry.Point;
    pointGraphic: __esri.Graphic;
    lineGraphic: __esri.Graphic;
    _county = null;
    @Input()
    set county(county) {
        this._county = county;
    }
    get county() {
        return this._county;
    }
    _route = null;
    @Input()
    set route(route) {
        this._route = route;
    }
    get route() {
        return this._route;
    }
    globalSegment = '';
    _segment = null;
    @Input()
    set segment(segment) {
        this._segment = segment;
        this.globalSegment = segment;
        this.loadIsleSegment();
        this.changeDetectorRef.detectChanges();
    }
    get segment() {
        return this._segment;
    }
    _lat = '';
    @Input()
    set lat(value) {
        this._lat = value;
    }
    get lat() {
        return this._lat;
    }
    _long = '';
    @Input()
    set long(value) {
        this._long = value;
    }
    get long() {
        return this._long;
    }
    @Input() segmentInfo: { number: string; latLongs: string } = null;
    @Input() templateName;

    _username = '';
    @Input()
    set username(value) {
        this._username = value;
    }
    get username() {
        return this._username;
    }
    @Input() tolerance: number = 100;

    //usecrsforislebot checks if county, route and segment is provided as input for ISLE bot. crs is county, route,segment.
    _usecrsforislebot: boolean = false;
    @Input()
    set usecrsforislebot(value) {
        this._usecrsforislebot = BooleanConverter(value);
    }
    get usecrsforislebot() {
        return this._usecrsforislebot;
    }

    _appEnvironment: string = '';
    @Input()
    set appEnvironment(value) {
        this._appEnvironment = value;
    }
    get appEnvironment() {
        return this._appEnvironment;
    }

    _enableMapClick = true;
    @Input()
    set enableMapClick(value: boolean) {
        this._enableMapClick = BooleanConverter(value);
    }
    get enableMapClick() {
        return this._enableMapClick;
    }

    @Output() readonly newsegmentevent = new EventEmitter<string>();
    @Output() readonly selectedTemplateChanged = new EventEmitter<string>();

    @ViewChild('primary') private readonly primaryDiv: ElementRef<HTMLDivElement>;
    @ViewChild('secondary') private readonly secondaryDiv: ElementRef<HTMLDivElement>;

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly isleService: IsleService,
        private readonly gisToolsService: GisToolsService
    ) {
        super(_vcr);
        this.registerEvent('activeChange', this.activeChange);
        this.registerEvent('newsegmentevent', this.newsegmentevent);
        this.registerEvent('selectedTemplateChanged', this.selectedTemplateChanged);
    }

    async ngOnInit() {
        try {
            this.mapRef = await this.setupMapViewRef(this.mapRef);
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    loadIsleSegment() {
        if (this.usecrsforislebot) {
            if (this.username) {
                this.getTemplateByUser(this.username, this.appEnvironment);
            }
            this.getSegmentDataForISLE();
            this.changeDetectorRef.detectChanges();
        }
    }

    async getTemplateByUser(username, appEnvironment) {
        const result = await this.isleService.getTemplates(username, appEnvironment);

        const updatedOptions = result;
        const finalOptions = [];

        /*only displays one template name if there are duplicate names of templates since
            original has id of ISLE_SYSTEM and custom made templates have user's username*/
        updatedOptions.forEach(template => {
            //checks that userid is a personal profile and not default system
            if (template.userId === username && template.userId !== 'ISLE_SYSTEM') {
                updatedOptions.forEach((template2, index2) => {
                    //removes the duplicate option from template list
                    if (template.name === template2.name && template.userId !== template2.userId) {
                        updatedOptions.splice(index2, 1);
                    }
                });
            }
        });

        //populates array order to be displayed in dropdown
        //checks if user is signed in
        if (username.length > 0 && username !== 'ISLE_SYSTEM') {
            //adds first option with empty vaule as empty placeholder
            finalOptions.push(updatedOptions[0]);

            //if the user has a template set as default, it will get added second
            updatedOptions.forEach(template => {
                if (template.isDefault && template.userId !== 'ISLE_SYSTEM') {
                    finalOptions.push(template);

                    //sets default template to be selected by default only if selected is pavement history
                    //will stay as pavement history if already is, or will change default
                    //need if statement so doesn't reset selected everytime a new segment is loaded
                    if (this.selectedTemplate === 'PAVEMENT HISTORY') {
                        this.selectedTemplate = template.name;
                    }
                }
            });

            /*if the above statement didn't populate a second template (meaning no default template selected)
                    then will push normal second template*/
            if (finalOptions.length === 1) {
                finalOptions.push(updatedOptions[1]);
            }

            //will push all remaining templates to array
            updatedOptions.forEach(template => {
                if (template.name !== finalOptions[0].name && template.name !== finalOptions[1].name) {
                    finalOptions.push(template);
                }
            });

        } else {
            //if user is not signed in
            updatedOptions.forEach(template => {
                finalOptions.push(template);
            });
        }

        //populates global array used by dropdown
        this.templateOptions = finalOptions.map(t => t.name);
    }

    async getSegmentDataForISLE() {
        this.getSegmentData();
        this.includeSegmentSection = true;
        this.showSelect = true;
    }
    /**
     * Make the tool active/shown.
     */
    private async activate() {
        await this.loadESRIClasses();
        this.getTemplateByUser(this.username, this.appEnvironment);

        if (!this.mapClickListener && this.enableMapClick) {
            while (!this.mapRef) {
                await util.sleep(10);
            }

            this.mapClickListener = this.mapRef.on('click', (evt) => this.handleMapClick(evt));
        }
    }

    /**
     * Make the tool inactive/hidden.
     */
    private deactivate(): void {
        if (this.mapClickListener) {
            this.mapClickListener.remove();
            this.mapClickListener = null;
        }
        if (this.lineGraphic) {
            this.mapRef.graphics.remove(this.lineGraphic);
        }
    }

    async loadESRIClasses() {
        if (!Point || !Graphic || !SpatialReference) {
            [
                Point,
                Graphic,
                SpatialReference
            ] = await Promise.all([
                ESRI.Point,
                ESRI.Graphic,
                ESRI.SpatialReference
            ]);
        }
    }

    private async handleMapClick(evt) {
        if (evt.mapPoint) {
            this.selectedPoint = evt.mapPoint;
            // this.options.crsTolerance = this.utilityService.getSearchRadiusTolerance(this.map.getZoom());
            this.lat = this.selectedPoint.latitude.toString();
            this.long = this.selectedPoint.longitude.toString();
            await this.getCSR(this.lat, this.long);
            await this.getSegmentData();
            this.addPoint(this.selectedPoint.longitude, this.selectedPoint.latitude);
            this.drawSegment();
            this.showSelect = true;
            this.changeDetectorRef.detectChanges();
        }
    }

    private async getSegmentData() {
        if (this.segment) {
            const [segmentData, segmentsData] = await Promise.all([
                this.isleService.getSegment(this.county, this.route, this.segment, this.selectedTemplate, this.username, this.usecrsforislebot, this.appEnvironment),
                this.isleService.getSegments(this.county, this.route)
            ]);

            this.segmentData = new SegmentData(segmentData);
            this.selectedSegment = this.segmentData.segments[0];
            if (typeof this.selectedSegment !== 'undefined') {
                this.segmentData.sideIndicator = this.selectedSegment.sideIndicator;
            }
            this.setSegmentData(this.selectedSegment, segmentsData as Segment[]);
        }
        this.changeDetectorRef.detectChanges();
    }

    async getCSR(lat, long, tolerance = null) {
        let segments: any;
        segments = await this.gisToolsService.getSegmentsNearLatLong(lat, long, tolerance || this.tolerance);
        if (segments.length) {
            this.county = segments[0].countyCode;
            this.segment = segments[0].segmentNumber;
            this.route = segments[0].stateRouteNumber;
            this.globalSegment = this.segment;
            this.segmentChanged(this.segment);
        }

    }

    segmentChanged(newSegment) {
        this.newsegmentevent.emit(newSegment);
    }

    private setSegmentData(segment: Segment, segments: Segment[]) {
        // Find previous segment to this one if it exists.
        segment.prev = segments.reduce((acc, s) => {
            if (s.sideIndicator === segment.sideIndicator && s.segmentNumber < segment.segmentNumber && (!acc || s.segmentNumber > acc.segmentNumber)) {
                return s;
            }
            return acc;
        }, null);

        // Find next segment if it exists.
        segment.next = segments.reduce((acc, s) => {
            if (s.sideIndicator === segment.sideIndicator && s.segmentNumber > segment.segmentNumber && (!acc || s.segmentNumber < acc.segmentNumber)) {
                return s;
            }
            return acc;
        }, null);

        // Find segment on opposite side of the road if it exists.
        segment.otherSide = segments.find((s, index) => {
            // Segment at 'index' is is otherSide if, based on sideIndicator,
            // the previous/next segment is our selected segment.
            const checkSegment = segments[segment.sideIndicator === '1' ? index - 1 : index + 1];
            const otherSideSegs = checkSegment && s.sideIndicator !== checkSegment.sideIndicator && checkSegment.segmentNumber === segment.segmentNumber;
            return otherSideSegs;
        });

        this.changeDetectorRef.detectChanges();
    }

    private addPoint(long: number, lat: number) {
        if (this.active && this.mapRef) {
            if (this.pointGraphic) {
                this.mapRef.graphics.remove(this.pointGraphic);
            }
            const point = new Point({
                longitude: parseFloat(<any>long),
                latitude: parseFloat(<any>lat)
            });
            this.centerPoint = point;
            this.pointGraphic = new Graphic({
                geometry: point,
                symbol: this._pointSymbol
            });

            this.mapRef.graphics.add(this.pointGraphic);

            if (this._moveMapOnClick) {
                if (this.mapRef.zoom < this.centerMapZoomLevel && this.enableAutoZoomLevel) {
                    this.mapRef.goTo({
                        center: this.centerPoint,
                        zoom: this.centerMapZoomLevel
                    });
                } else {
                    this.mapRef.goTo({
                        center: this.centerPoint
                    });
                }
            }

            this.changeDetectorRef.detectChanges();
        }
    }

    private drawSegment() {
        if (this.selectedSegment) {
            if (this.lineGraphic) {
                this.mapRef.graphics.remove(this.lineGraphic);
            }
            const points: number[][] = [];
            const latLongs = (<string>this.selectedSegment.latLongs).split(';');
            for (const latLong of latLongs) {
                const parts = latLong.split(',');
                points.push([parseFloat(parts[1]), parseFloat(parts[0])]);
            }
            const polyline = {
                type: 'polyline',
                paths: points,
                spatialReference: null
            };
            this.lineGraphic = new Graphic({
                geometry: polyline,
                symbol: this._lineSymbol
            });

            this.mapRef.graphics.add(this.lineGraphic);

            // this.centerPoint = graphic.geometry.extent.center;
            this.mapRef.goTo({
                center: this.centerPoint
            });
        }
    }

    public async updateSegment() {
        if (!this.usecrsforislebot) {
            this.lat = this.selectedPoint.latitude.toString();
            this.long = this.selectedPoint.longitude.toString();
            await this.getCSR(this.lat, this.long);
            await this.getSegmentData();
            this.addPoint(this.selectedPoint.longitude, this.selectedPoint.latitude);
            this.drawSegment();
            this.changeDetectorRef.detectChanges();
        } else {
            await this.getSegmentData();
        }
    }

    public async templateChanged() {
        this.updateSegment();
        this.selectedTemplateChanged.emit(this.selectedTemplate);
    }

    public selectItemDetails(event: any, item: any) {
        this.selectedAttributeItem = item;
        this.itemDetailsOpen = true;
        this.changeDetectorRef.detectChanges();

        // Need to set location after displaying otherwise secondaryDiv size isn't calculated
        // (event.page_ - event._) accounts for amount scrolled
        // Display to left/right of click location depending on location of click in relation to size of container
        const primaryDivContainer = this.primaryDiv.nativeElement.getBoundingClientRect();
        const leftPosition = event.pageX < (primaryDivContainer.right / 2)
            ? event.clientX
            : event.screenX - (event.screenX - event.clientX + this.secondaryDiv.nativeElement.clientWidth) + (event.pageX - event.x);
        this.secondaryDiv.nativeElement.style.left = `${leftPosition}px`;
        this.secondaryDiv.nativeElement.style.top = `${event.screenY - (event.screenY - event.clientY + this.secondaryDiv.nativeElement.clientHeight) + (event.pageY - event.y)}px`;
    }

    public hideItemDetails() {
        this.selectedAttributeItem = null;
        this.itemDetailsOpen = false;
        this.changeDetectorRef.detectChanges();
    }

    public async changeSegment(segment: ISegmentData) {
        this.mileMarkerDescription = '';
        const latLongs = segment.latLongs.split(';');
        const midPt = util.getMiddleItem(latLongs);
        if (midPt) {
            this.lat = midPt.split(',')[0];
            this.long = midPt.split(',')[1];
        } else {
            this.lat = '';
            this.long = '';
        }

        if (!this.usecrsforislebot) {
            await this.getCSR(this.lat, this.long, 5);
            await this.getSegmentData();
            this.drawSegment();
            this.addPoint(parseFloat(this.long), parseFloat(this.lat));
        } else {
            this.segment = segment.segmentNumber;
            this.segmentChanged(this.segment);
        }
        this.changeDetectorRef.detectChanges();
    }

    public async switchSides(segment: Segment) {
        this.selectedSegment = segment;
        await this.changeSegment(segment);
        this.changeDetectorRef.detectChanges();
    }
}
